import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Define types for messages
interface Message {
  role: string;
  content: string;
  sentiment?: string;
  timestamp: string;
}

interface StreamPayload {
  fullText: string;
  sentiment: string;
}

// Define the state type
interface ChatState {
  messages: Message[];
  isTyping: boolean;
  currentAudio: string | null;
  streamingText: string;
  isStreaming: boolean;
  currentSentiment: string;
}

const initialState: ChatState = {
  messages: [],
  isTyping: false,
  currentAudio: null,
  streamingText: '',
  isStreaming: false,
  currentSentiment: 'neutral'
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload)
    },
    addStreamChunk: (state, action: PayloadAction<string>) => {
      state.streamingText += action.payload
      state.isStreaming = true
    },
    completeStream: (state, action: PayloadAction<StreamPayload>) => {
      if (state.streamingText) {
        state.messages.push({
          role: 'assistant',
          content: action.payload.fullText,
          sentiment: action.payload.sentiment,
          timestamp: new Date().toISOString()
        })
        state.streamingText = ''
        state.isStreaming = false
        state.currentSentiment = action.payload.sentiment
      }
    },
    setTyping: (state, action: PayloadAction<boolean>) => {
      state.isTyping = action.payload
    },
    setAudio: (state, action: PayloadAction<string | null>) => {
      state.currentAudio = action.payload
    },
    setSentiment: (state, action: PayloadAction<string>) => {
      state.currentSentiment = action.payload
    },
    restoreMessages: (state, action: PayloadAction<Message[]>) => {
      // Filter out system messages for UI display
      state.messages = action.payload.filter((msg: Message) => msg.role !== 'system')
    },
    clearChat: (state) => {
      state.messages = []
      state.streamingText = ''
      state.isStreaming = false
      state.isTyping = false
      state.currentAudio = null
    }
  }
})

export const {
  addMessage,
  addStreamChunk,
  completeStream,
  setTyping,
  setAudio,
  setSentiment,
  restoreMessages,
  clearChat
} = chatSlice.actions

export default chatSlice.reducer
